import React from 'react';
import { Box, Container, Typography, Link, Divider, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const LAST_UPDATED = 'June 3, 2026';

const SECTIONS = [
  {
    heading: '1. Information We Collect',
    body: [
      'Account details you provide: name, phone number, email address (optional for staff), and city. Students register with a phone number; librarians, institute managers, and other staff register with an email or phone number.',
      'Booking and usage data: libraries and seats you book, check-in/check-out sessions, subscriptions, study statistics, ratings, and notes recorded by your librarian.',
      'Payment data: wallet top-ups and subscription purchases are processed by Razorpay. We store transaction records and references, but card and bank details are handled directly by Razorpay and never stored on our servers.',
      'Device and analytics data: pages visited, dwell time, referrer, approximate location (country/state/city derived from IP), browser, operating system, and UTM campaign tags. A first-party cookie holds a randomly generated visitor identifier for up to one year.',
    ],
  },
  {
    heading: '2. How We Use Your Information',
    body: [
      'To create and manage your account, process bookings, check-ins, and subscriptions.',
      'To process payments and maintain your wallet balance through Razorpay.',
      'To send transactional messages — OTPs, booking confirmations, and reminders — over WhatsApp and in-app/web push notifications.',
      'To operate gamification features such as BML Coins, and to show relevant libraries, institutes, banners, and offers.',
      'To measure platform performance, understand usage, prevent abuse, and improve our services.',
    ],
  },
  {
    heading: '3. Sharing of Information',
    body: [
      'Librarians and institute managers can see the booking, attendance, and contact details of students associated with their library or institute.',
      'We share data with service providers strictly to operate the platform: Razorpay (payments), our WhatsApp messaging provider (OTP and notifications), and web push services.',
      'We do not sell your personal information. We may disclose information where required by law or to protect the rights, safety, and security of our users and platform.',
    ],
  },
  {
    heading: '4. OTP and Messaging',
    body: [
      'One-time passwords are generated and verified on our own servers; our messaging provider only delivers the message. Standard WhatsApp and carrier terms apply to messages you receive.',
    ],
  },
  {
    heading: '5. Cookies and Analytics',
    body: [
      'We use a first-party cookie to recognise returning visitors and to measure traffic. We honour the browser "Do Not Track" (DNT) signal — when DNT is enabled, analytics events are not recorded.',
      'You can clear cookies through your browser at any time; doing so resets your anonymous visitor identifier.',
    ],
  },
  {
    heading: '6. Data Retention',
    body: [
      'Account records are retained for as long as your account is active. Analytics session, page-view, and event data are retained for a limited rolling window (by default one year) and then aggregated or deleted.',
    ],
  },
  {
    heading: '7. Data Security',
    body: [
      'Passwords are hashed, access is protected with signed tokens, and traffic is served over HTTPS. No method of transmission or storage is completely secure, but we take reasonable measures to protect your information.',
    ],
  },
  {
    heading: '8. Your Rights',
    body: [
      'You may access or update your profile information from within the app. To request correction or deletion of your data, contact us using the details below. Note that some records (such as payment and tax records) may be retained where the law requires.',
    ],
  },
  {
    heading: '9. Children',
    body: [
      'Our services are intended for users who can lawfully enter into agreements and use study libraries. If you believe a minor has provided us personal information without appropriate consent, please contact us.',
    ],
  },
  {
    heading: '10. Changes to This Policy',
    body: [
      'We may update this Privacy Policy from time to time. Material changes will be reflected by updating the "Last updated" date at the top of this page.',
    ],
  },
];

/**
 * PrivacyPage — public privacy policy.
 * Renders inside AppShell (Aurora background + Footer).
 */
function PrivacyPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const muted = isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)';

  return (
    <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
      <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.9rem', md: '2.4rem' } }}>
        Privacy Policy
      </Typography>
      <Typography variant="body2" sx={{ color: muted, mb: 4 }}>
        Last updated: {LAST_UPDATED}
      </Typography>

      <Typography variant="body1" sx={{ color: muted, lineHeight: 1.8, mb: 4 }}>
        This Privacy Policy explains how BookMyLibrary, operated by J2 Learning Pvt. Ltd. ("we",
        "us", "our"), collects, uses, and protects your information when you use our platform to
        discover, book, and manage seats at study libraries and coaching institutes across India.
      </Typography>

      {SECTIONS.map((section) => (
        <Box key={section.heading} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            {section.heading}
          </Typography>
          {section.body.map((paragraph, i) => (
            <Typography
              key={i}
              variant="body1"
              sx={{ color: muted, lineHeight: 1.8, mb: 1.5 }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>
      ))}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          11. Contact Us
        </Typography>
        <Typography variant="body1" sx={{ color: muted, lineHeight: 1.8 }}>
          For any questions about this Privacy Policy or your data, email us at{' '}
          <Link href="mailto:support@bookmylibrary.in" underline="hover" sx={{ fontWeight: 600 }}>
            support@bookmylibrary.in
          </Link>
          .
        </Typography>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* DB-IP attribution — required on the public privacy policy page (see CLAUDE.md §10). */}
      <Typography variant="caption" sx={{ color: muted, display: 'block', mb: 3 }}>
        IP geolocation by{' '}
        <Link href="https://db-ip.com" target="_blank" rel="noopener noreferrer" underline="hover">
          DB-IP
        </Link>
        , licensed under CC BY 4.0.
      </Typography>

      <Link component={RouterLink} to="/" underline="hover" sx={{ fontWeight: 600 }}>
        ← Back to home
      </Link>
    </Container>
  );
}

export default PrivacyPage;
